// BUILD THE SCHEDULE FROM THE PROJECT'S LEVELS, AREAS & SYSTEMS
// (back/utils/systemTasks; owner, 2026-10-15: "make this a default build
// from project spatial/system button on the schedule template ... bim
// parent, modeling, coordination, shopdrawings as children and field parent
// by having install as child and all levels, under them all areas, under
// them all systems", each system an equal, consecutive share of its area's
// working days).
//
// One dialog, three moments:
//   1. READINESS. The tab is checked first ("check if the levels, areas,
//      systems has valid data first, otherwise direct the user"): what is
//      missing is named per level and area, each with the button that fixes
//      it. A partly ready tab offers to build the ready areas only - the
//      default is to fix the data first.
//   2. PREVIEW. Grouped step > level > area: every system task with its
//      dates and whether it is new, adopted, re-dated or waiting for a
//      decision. Decisions are ticks: re-date a row that already has
//      progress, add tasks to a finished area, remove a row the tab no
//      longer lists.
//   3. RESULT, with Undo.
// Nothing is written before "Build"; the server recomputes the previewed
// plan and refuses if it changed in between.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Checkbox, Collapse, Empty, InputNumber, Modal, Space, Spin, Table, Tag } from "antd";
import { previewSystemTasks, applySystemTasks, undoSystemTasks } from "api/gantt/ganttApi";

const STATE = {
  new: { color: "green", text: "new" },
  adopted: { color: "blue", text: "adopted" },
  redated: { color: "geekblue", text: "adopted, new dates" },
  unchanged: { color: "default", text: "unchanged" },
  needs_decision: { color: "orange", text: "has progress - kept" },
  held: { color: "gold", text: "area complete - not added" },
};
const READY = {
  ready: { color: "green", text: "ready" },
  partial: { color: "gold", text: "partly ready" },
  not_ready: { color: "red", text: "not ready" },
  no_areas: { color: "red", text: "no areas" },
  no_systems: { color: "red", text: "no systems" },
  pending_approval: { color: "orange", text: "waiting for approval" },
  subarea: { color: "default", text: "subarea - left alone" },
};
const STEP_DAYS = [
  ["model", "Modeling"], ["coordinate", "Coordination"], ["shop_drawings", "Shop drawings"], ["install", "Install"],
];
const plural = (n, one, many) => `${n} ${n === 1 ? one : many || `${one}s`}`;

function Remedy({ r }) {
  return (
    <li style={{ marginBottom: 6 }} data-testid={`remedy-${r.code}`}>
      {r.text}{" "}
      <a href={r.link} target="_blank" rel="noopener noreferrer">{r.action}</a>
      {r.then && <> {" · then "}<a href={r.then.link} target="_blank" rel="noopener noreferrer">{r.then.action}</a></>}
    </li>
  );
}

function Readiness({ readiness }) {
  if (!readiness) return null;
  return (
    <div data-testid="systasks-readiness">
      {readiness.levels.map((l) => (
        <div key={l.pageId} style={{ marginBottom: 6 }}>
          <b>{l.name}</b> <Tag color={(READY[l.status] || {}).color}>{(READY[l.status] || {}).text || l.status}</Tag>
          <div style={{ fontSize: 12, color: "#555", marginLeft: 12 }}>
            {l.areas.map((a) => (
              <span key={a.areaId} style={{ marginRight: 10, whiteSpace: "nowrap" }}>
                {a.label} <Tag color={(READY[a.status] || {}).color} style={{ marginRight: 2 }}>{(READY[a.status] || {}).text || a.status}</Tag>
                {a.status === "ready" ? <span style={{ color: "#888" }}>({a.systems.join(", ")})</span> : null}
              </span>
            ))}
          </div>
        </div>
      ))}
      {readiness.remedies.length > 0 && (
        <>
          <div style={{ fontWeight: 600, marginTop: 8 }}>What to fix</div>
          <ul style={{ paddingLeft: 18, marginBottom: 0 }}>{readiness.remedies.map((r, i) => <Remedy key={`${r.code}-${r.areaId || r.level || i}`} r={r} />)}</ul>
        </>
      )}
    </div>
  );
}

export default function SystemTasksBuilder({ projectId, open, onClose, onApplied }) {
  const [opts, setOpts] = useState({ startDate: "", areaDays: {}, onlyReady: false, redate: [], include: [], remove: [] });
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const run = useCallback(async (o) => {
    setBusy(true); setError("");
    try {
      setPlan(await previewSystemTasks(projectId, { ...o, startDate: o.startDate || undefined }));
    } catch (e) {
      setError(e.message || "Could not check the levels, areas and systems");
    } finally { setBusy(false); }
  }, [projectId]);

  useEffect(() => {
    if (!open) return;
    setResult(null); setPlan(null);
    const fresh = { startDate: "", areaDays: {}, onlyReady: false, redate: [], include: [], remove: [] };
    setOpts(fresh);
    run(fresh);
  }, [open, run]);

  const change = (patch) => { const next = { ...opts, ...patch }; setOpts(next); run(next); };
  const toggle = (key, id, on) => change({ [key]: on ? [...opts[key], id] : opts[key].filter((x) => x !== id) });

  const build = async () => {
    setBusy(true); setError("");
    try {
      const r = await applySystemTasks(projectId, {
        ...opts, startDate: opts.startDate || undefined, planHash: plan.planHash, readyPairs: plan.readiness.readyPairs,
      });
      setResult(r);
      if (onApplied) onApplied(r);
    } catch (e) {
      setError(e.message || "Could not build the schedule");
      if (e.data && e.data.plan) setPlan(e.data.plan);
    } finally { setBusy(false); }
  };
  const undo = async (force = false) => {
    setBusy(true); setError("");
    try {
      const r = await undoSystemTasks(projectId, result.runId, force);
      setResult({ ...result, undone: r });
      if (onApplied) onApplied(null);
    } catch (e) {
      setError(e.message || "Could not undo");
      if (e.data && e.data.code === "edited_since") setResult({ ...result, editedSince: true });
    } finally { setBusy(false); }
  };

  const rd = plan && plan.readiness;
  const refusedHard = plan && plan.refused && plan.refused.code !== "needs_start";
  const canBuild = plan && !refusedHard && !(plan.needsStart && !opts.startDate) && (!plan.partial || opts.onlyReady)
    && plan.counts && (plan.counts.create + plan.counts.redate + plan.counts.adopt + plan.counts.remove) > 0;

  const panels = useMemo(() => {
    if (!plan || !plan.tree) return [];
    const out = [];
    for (const ph of plan.tree) {
      for (const st of ph.children || []) {
        out.push({ key: st.id, label: `${ph.name} › ${st.name}`, step: st });
      }
    }
    return out;
  }, [plan]);

  const areaTable = (ar) => {
    const rows = (ar.children || []).map((s, i) => ({ ...s, n: i + 1, key: s.id }));
    return (
      <div key={ar.id} style={{ marginBottom: 12 }} data-testid={`systasks-area-${ar.id}`}>
        <div style={{ fontWeight: 600 }}>
          {ar.name} <span style={{ fontWeight: 400, color: "#666" }}>{ar.start} → {ar.end}</span>{" "}
          <Tag color={(STATE[ar.state] || {}).color}>{ar.state === "new" ? "new area" : "on the schedule"}</Tag>
          {ar.progress && <span style={{ fontSize: 12, color: "#b45309" }}> progress {ar.progress.before}% → {ar.progress.after}%</span>}
          {ar.overflow && <Tag color="red">more systems than working days</Tag>}
        </div>
        {ar.held && (
          <Checkbox checked={opts.include.includes(ar.id)} onChange={(e) => toggle("include", ar.id, e.target.checked)} data-testid={`include-${ar.id}`}>
            This area is complete. Add its system tasks anyway (they are added as done)
          </Checkbox>
        )}
        <Table size="small" pagination={false} dataSource={rows} columns={[
          { title: "#", dataIndex: "n", width: 36 },
          { title: "System", dataIndex: "system" },
          { title: "Dates", render: (s) => `${s.start} → ${s.end}` },
          { title: "Days", dataIndex: "days", width: 56, render: (d) => d || "" },
          { title: "", render: (s) => <Tag color={(STATE[s.state] || {}).color}>{(STATE[s.state] || {}).text || s.state}</Tag> },
          {
            title: "Note",
            render: (s) => (
              <span style={{ fontSize: 12 }}>
                {s.from && (s.from.start !== s.start || s.from.end !== s.end) ? `was ${s.from.start} → ${s.from.end}. ` : ""}
                {s.decision ? `${s.decision}. ` : ""}
                {(s.flags || []).join("; ")}
                {s.state === "needs_decision" && (
                  <div><Checkbox checked={opts.redate.includes(s.id)} onChange={(e) => toggle("redate", s.id, e.target.checked)} data-testid={`redate-${s.id}`}>Give it its share anyway</Checkbox></div>
                )}
              </span>
            ),
          },
        ]} />
        {(ar.notInTab || []).map((r) => (
          <div key={r.id} style={{ fontSize: 12, color: "#92400e" }}>
            <Checkbox checked={opts.remove.includes(r.id)} onChange={(e) => toggle("remove", r.id, e.target.checked)} data-testid={`remove-${r.id}`}>
              Remove "{r.name}" - no longer a system of this area on the tab{r.actuals ? " (it has progress)" : ""}
            </Checkbox>
          </div>
        ))}
        {(ar.leftAlone || []).length > 0 && (
          <div style={{ fontSize: 12, color: "#888" }}>Left as they are: {ar.leftAlone.map((r) => `${r.name} (${r.why})`).join("; ")}</div>
        )}
      </div>
    );
  };

  const c = (plan && plan.counts) || {};
  return (
    <Modal
      title="Build from project levels, areas & systems"
      open={open}
      onCancel={onClose}
      width={980}
      footer={result ? [
        <Button key="close" onClick={onClose}>Close</Button>,
      ] : [
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        rd && rd.status !== "ready" && (
          <Button key="fix" type={plan && plan.partial && opts.onlyReady ? "default" : "primary"} href={`/areas/${projectId}`} target="_blank">
            Fix Levels, Areas & Systems first
          </Button>
        ),
        <Button key="build" type="primary" disabled={!canBuild || busy} loading={busy} onClick={build} data-testid="systasks-build">
          Build
        </Button>,
      ].filter(Boolean)}
    >
      <Spin spinning={busy}>
        {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 10 }} />}
        {result ? (
          <div data-testid="systasks-result">
            {result.undone ? (
              <Alert type="info" showIcon message={`Undone: ${plural(result.undone.restored, "row")} put back, ${plural(result.undone.removed, "added row")} taken away.`} />
            ) : result.nothingToDo ? (
              <Alert type="info" showIcon message="Nothing to change - the schedule already matches the tab." />
            ) : (
              <Alert
                type="success" showIcon
                message={`Built: ${plural(result.created, "row")} added, ${plural(result.updated, "row")} updated, ${plural(result.removed, "row")} removed.`}
                description={(
                  <Space>
                    <span>Saved to the schedule.</span>
                    <Button size="small" onClick={() => undo(false)} data-testid="systasks-undo">Undo</Button>
                    {result.editedSince && <Button size="small" danger onClick={() => undo(true)}>Undo anyway</Button>}
                  </Space>
                )}
              />
            )}
          </div>
        ) : !plan ? (busy ? <div style={{ minHeight: 80 }} /> : <Empty description="Checking the levels, areas and systems" />) : (
          <>
            {rd.status !== "ready" && (
              <Alert
                type={rd.status === "partial" ? "warning" : "error"} showIcon style={{ marginBottom: 10 }}
                message={rd.status === "partial"
                  ? `Only ${rd.readyAreas} of ${rd.countedAreas} areas are ready. Fixing the rest first is the safer choice.`
                  : (plan.refused && plan.refused.text) || "Levels, Areas & Systems is not ready"}
                data-testid="systasks-not-ready"
              />
            )}
            <Readiness readiness={rd} />
            {plan.partial && (
              <Checkbox style={{ marginTop: 8 }} checked={opts.onlyReady} onChange={(e) => change({ onlyReady: e.target.checked })} data-testid="systasks-only-ready">
                Build the {plural(rd.readyAreas, "ready area")} now and skip the rest
              </Checkbox>
            )}
            {!refusedHard && (
              <>
                {(plan.needsStart || opts.startDate) && (
                  <div style={{ marginTop: 12, padding: 8, background: "#fafafa", border: "1px solid #eee" }} data-testid="systasks-start">
                    <div style={{ fontSize: 12, color: "#555", marginBottom: 6 }}>
                      Some rows are new and have no dated parent. They are laid out from this date: areas one after another
                      within a level, levels one after another, steps one after another, BIM before Field.
                    </div>
                    <Space wrap>
                      <span>Start</span>
                      <input type="date" value={opts.startDate} onChange={(e) => change({ startDate: e.target.value })}
                             style={{ border: "1px solid #d9d9d9", borderRadius: 6, padding: "3px 8px" }} data-testid="systasks-start-date" />
                      {STEP_DAYS.map(([k, label]) => (
                        <span key={k}>
                          {label}{" "}
                          <InputNumber size="small" min={1} max={1000} value={opts.areaDays[k] || (plan.areaDays || {})[k]}
                                       onChange={(v) => change({ areaDays: { ...opts.areaDays, [k]: v } })} /> days per area
                        </span>
                      ))}
                    </Space>
                  </div>
                )}
                <div style={{ margin: "12px 0 8px" }} data-testid="systasks-summary">
                  {plural(c.systemTasks || 0, "system task")} - {c.create || 0} new rows, {c.redate || 0} existing rows get new dates,
                  {" "}{c.unchanged || 0} unchanged. {c.decisions ? `${plural(c.decisions, "row")} with progress kept on their dates. ` : ""}
                  {c.held ? `${plural(c.held, "task")} not added to finished areas. ` : ""}
                  {c.notInTab ? `${plural(c.notInTab, "row")} no longer on the tab (kept unless ticked). ` : ""}
                  Each system gets an equal share of its area's working days, in the tab's order ({plan.calendar && plan.calendar.name}).
                </div>
                <Collapse items={panels.map((p) => ({
                  key: p.key,
                  label: <span>{p.label} <span style={{ color: "#888" }}>{p.step.start} → {p.step.end}</span></span>,
                  children: (p.step.children || []).map((lv) => (
                    <div key={lv.id} style={{ marginBottom: 8 }}>
                      <div style={{ fontWeight: 700, margin: "4px 0" }}>{lv.name}</div>
                      {(lv.children || []).map(areaTable)}
                    </div>
                  )),
                }))} />
              </>
            )}
          </>
        )}
      </Spin>
    </Modal>
  );
}
